var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { getVisibleError, handleWidgetUpdate, renderForm, validateCustom } from '@juvare/common-form-ui/WidgetForm/Utils';
import { isEmailValid } from '@juvare/common-form-ui/WidgetFormEmail';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { parsePhoneNumber } from '@juvare/common-phone-number';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { showDataImportDialog } from '@juvare/data-import-ui/Dialogs';
import { useFunctionClient, useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, FormControl, IconButton, Link, TextField, Typography } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation } from '../../../../hooks';
import { type } from './types';
const delimiter = ';';
const getValue = (value) => (value ? value.trim() : '');
const getArrayValue = (value) => value && value.trim()
    ? value
        .split(delimiter)
        .map(getValue)
        .filter((value) => !!value)
    : [];
const csvTemplate = `LAST_NAME,FIRST_NAME,CONTACT_EMAIL,CONTACT_PHONE_NUMBER_TEXT,CONTACT_PHONE_NUMBER_VOICE,CONTACT_PHONE_NUMBER_FAX,CONTACT_PAGER_NUMBER
Smith,John,JohnDoe@example.com;JaneDoe@example.com,+1XXXXXXXXXX;+1XXXXXXXXXX,+1XXXXXXXXXX;+1XXXXXXXXXX,+1XXXXXXXXXX;+1XXXXXXXXXX,1234567890@example.com
Doe,Jane,JaneDoe@example.com,+1XXXXXXXXXX,+1XXXXXXXXXX,+1XXXXXXXXXX,1234567890@example.com;1234567891@example.com`;
const WidgetExternalRecipients = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const { t } = useAlertUiTranslation();
    const error = getVisibleError(props);
    const functionClient = useFunctionClient();
    const usePagerCarrierStorage = useStorageClient('pagerCarrier');
    const handler = new WidgetHandler(props);
    const accountsCount = ((_a = props.getData()) === null || _a === void 0 ? void 0 : _a.rowCount) > 0 ? `Accounts: ${(_b = props.getData()) === null || _b === void 0 ? void 0 : _b.rowCount}` : undefined;
    const handleRemoveExternalRecipients = () => __awaiter(void 0, void 0, void 0, function* () {
        props.setData(undefined);
        props.rootUpdateAll();
        handler.setShowErrorsOverride('all');
        renderForm(props, { changed: true, autoSaveImmediately: true });
    });
    const handleOnClick = () => {
        const importId = crypto.randomUUID();
        let importRow = 0;
        showDataImportDialog({
            title: t('wizard.addRecipients.recipientsImportTitle'),
            init: () => __awaiter(void 0, void 0, void 0, function* () {
                return yield usePagerCarrierStorage.list({ filter: [{ column: 'status', op: '=', value: 'active' }] });
            }),
            validateRow: (row, csvFile, context) => __awaiter(void 0, void 0, void 0, function* () {
                if (!row.data.find((value) => !!value)) {
                    row.object = undefined;
                    return row;
                }
                const contactLastName = row.data[0];
                const contactFirstName = row.data[1];
                const contactEmails = getArrayValue(row.data[2]);
                const contactPhoneText = getArrayValue(row.data[3]);
                const contactPhoneVoice = getArrayValue(row.data[4]);
                const contactPhoneFax = getArrayValue(row.data[5]);
                const contactPagers = getArrayValue(row.data[6]);
                if (!contactLastName) {
                    row.errors.push(t('recipientImport.requiredField', { fieldName: 'LAST_NAME' }));
                }
                if (!contactFirstName) {
                    row.errors.push(t('recipientImport.requiredField', { fieldName: 'FIRST_NAME' }));
                }
                if (contactEmails.length) {
                    contactEmails.forEach((value) => {
                        if (!isEmailValid(value)) {
                            row.errors.push(t('recipientImport.invalidEmail', { email: value }));
                        }
                    });
                }
                else if (!contactEmails.length) {
                    row.errors.push(t('recipientImport.requiredField', { fieldName: 'CONTACT_EMAIL' }));
                }
                const contactsTextNumbers = contactPhoneText.map((phoneNumber) => {
                    const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
                    if (!parsedPhoneNumber) {
                        row.errors.push(t('recipientImport.invalidPhoneNumber', { phoneNumber }));
                    }
                    return {
                        phoneNumber: parsePhoneNumber(phoneNumber),
                        type: { fax: false, text: true, voice: false },
                    };
                });
                const contactsVoiceNumbers = contactPhoneVoice.map((phoneNumber) => {
                    const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
                    if (!parsedPhoneNumber) {
                        row.errors.push(t('recipientImport.invalidPhoneNumber', { phoneNumber }));
                    }
                    return {
                        phoneNumber: parsePhoneNumber(phoneNumber),
                        type: { fax: false, text: false, voice: true },
                    };
                });
                const contactsFaxNumbers = contactPhoneFax.map((phoneNumber) => {
                    const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
                    if (!parsedPhoneNumber) {
                        row.errors.push(t('recipientImport.invalidPhoneNumber', { phoneNumber }));
                    }
                    return {
                        phoneNumber: parsePhoneNumber(phoneNumber),
                        type: { fax: true, text: false, voice: false },
                    };
                });
                for (const pager of contactPagers) {
                    const carrierDomainName = pager.substring(pager.indexOf('@') + 1);
                    const filteredPagerCarriers = context.rows.filter((item) => item.columns.domainName === carrierDomainName);
                    if (filteredPagerCarriers.length === 0) {
                        row.errors.push(t('recipientImport.carrierDoesNotExist', { carrier: carrierDomainName }));
                    }
                }
                const contactsPagerNumbers = contactPagers.map((pager) => {
                    const phoneNumber = pager.substring(0, pager.indexOf('@'));
                    const carrierDomainName = pager.substring(pager.indexOf('@') + 1);
                    const pagerIsInvalid = pager.indexOf('@') === -1;
                    if (pagerIsInvalid) {
                        row.errors.push(t('recipientImport.invalidPager', { pager: pager }));
                    }
                    else if (carrierDomainName.length === 0) {
                        row.errors.push(t('recipientImport.invalidDomainName'));
                    }
                    if (phoneNumber.length !== 10) {
                        row.errors.push(t('recipientImport.invalidPagerNumberLength', { pagerNumber: phoneNumber }));
                    }
                    if (!phoneNumber.match(/^\d+$/)) {
                        row.errors.push(t('recipientImport.invalidPagerNumber', { pagerNumber: phoneNumber }));
                    }
                    return { number: phoneNumber, carrier: carrierDomainName };
                });
                importRow++;
                return Object.assign(Object.assign({}, row), { object: {
                        importId,
                        importRow,
                        lastName: contactLastName,
                        firstName: contactFirstName,
                        contactEmails,
                        contactPhoneNumbers: contactsTextNumbers.concat(contactsVoiceNumbers, contactsFaxNumbers),
                        pagerNumbers: contactsPagerNumbers,
                    } });
            }),
            importRows: (rows) => __awaiter(void 0, void 0, void 0, function* () {
                const chunkSize = 1000;
                for (let i = 0; i < rows.length; i += chunkSize) {
                    const recipientChunk = rows.slice(i, i + chunkSize);
                    yield functionClient.executeFunction({
                        functionName: 'saveImportedRecipients',
                        params: { rows: recipientChunk.map((recipient) => recipient.object) },
                    });
                }
                return rows;
            }),
            onAfterImport: (result) => __awaiter(void 0, void 0, void 0, function* () {
                props.setData({ importId, rowCount: result.rows.length });
                renderForm(props, { changed: true, autoSaveImmediately: true });
                return undefined;
            }),
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(FormControl, { variant: 'outlined', fullWidth: true, required: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.required, error: !!error },
            React.createElement(Box, { display: 'flex', alignItems: 'center' },
                React.createElement(TextField, { "data-testid": 'alert-external-recipients', size: 'medium', label: t('wizard.addRecipients.uploadCSV'), type: 'text', fullWidth: true, color: 'primary', variant: 'outlined', error: !!error, value: accountsCount || '', disabled: (_d = props.config) === null || _d === void 0 ? void 0 : _d.disabled, inputProps: { 'data-testid': 'alert-external-recipients', 'aria-label': 'External recipient selection' }, autoComplete: 'off', InputLabelProps: { shrink: accountsCount !== undefined }, sx: { input: { cursor: 'pointer', backgroundColor: 'background.paper' } }, InputProps: { readOnly: true }, onClick: handleOnClick }),
                accountsCount === undefined ? (React.createElement(IconButton, { color: 'primary', "aria-label": t('wizard.addRecipients.addExternalRecipients'), role: 'button', disabled: (_e = props.config) === null || _e === void 0 ? void 0 : _e.disabled, sx: { p: 1, ml: 2 }, "data-testid": 'add-external-recipients', onClick: handleOnClick },
                    React.createElement(FileUploadOutlinedIcon, { fontSize: 'medium' }))) : (React.createElement(IconButton, { color: 'primary', "aria-label": t('wizard.addRecipients.removeExternalRecipients'), role: 'button', sx: { p: 1, ml: 2 }, "data-testid": 'remove-external-recipients', onClick: handleRemoveExternalRecipients, disabled: !accountsCount || ((_f = props.config) === null || _f === void 0 ? void 0 : _f.disabled) },
                    React.createElement(RemoveCircleOutlineIcon, { fontSize: 'medium' })))),
            React.createElement(FormHelperErrorDescription, { error: error })),
        React.createElement(Typography, { sx: { fontSize: 12, color: themeColors.text.subtle, pt: 2, pb: 2 } },
            t('wizard.addRecipients.importFileExampleLabel'),
            ' ',
            React.createElement(Link, { sx: { textDecoration: 'underline' }, download: 'ExternalRecipientImportTemplate.csv', href: stringifyCsvFileToDataUrl(csvTemplate) }, t('wizard.addRecipients.importFileExampleLinkText')))));
};
function stringifyCsvFileToDataUrl(csvContent) {
    return 'data:application/octet-stream,' + encodeURIComponent(csvContent);
}
export const WidgetExternalRecipientsEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props),
        React.createElement(WidgetExternalRecipients, Object.assign({}, props))));
};
export const widgetExternalRecipients = {
    type,
    createWidget: () => ({ type, key: 'externalRecipients' }),
    load: (props) => {
        const handler = new WidgetHandler(props);
        if (!handler.formParams.features.enableExternalRecipients) {
            props.setData(undefined);
        }
    },
    validate: (props) => {
        const value = props.getData();
        return validateCustom(props, value);
    },
    icon: React.createElement(PersonAddAltIcon, null),
    editorComponent: WidgetExternalRecipientsEditor,
    update: handleWidgetUpdate,
    component: WidgetExternalRecipients,
};
